import { useState, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from 'react';
import type { Ambassador, Stage } from '../domain/types';
import { C, R, STAGE_CHIP, font } from '../theme/tokens';
import { PHOTOS, photoSrcSet, photoUrl, type PhotoId } from '../data/photos';

type Variant = 'primary' | 'ink' | 'white' | 'outline' | 'ghostDark' | 'soft' | 'mark' | 'success' | 'muted';
const VARIANTS: Record<Variant, { bg: string; fg: string; bd: string }> = {
  primary: { bg: C.violet, fg: C.white, bd: C.violet },
  ink: { bg: C.ink, fg: C.white, bd: C.ink },
  white: { bg: C.white, fg: C.ink, bd: C.white },
  outline: { bg: C.white, fg: C.ink, bd: C.border },
  ghostDark: { bg: 'transparent', fg: C.white, bd: C.darkBorder },
  soft: { bg: C.soft, fg: C.violet, bd: C.accentBorder },
  mark: { bg: C.mark, fg: C.ink, bd: C.mark },
  success: { bg: C.green, fg: C.white, bd: C.green },
  muted: { bg: C.border, fg: C.white, bd: C.border },
};
const HEIGHT = { lg: 54, md: 44, sm: 40, xs: 36 } as const;
const SIZE = { lg: 16, md: 13, sm: 13, xs: 12 } as const;

interface BtnProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  variant?: Variant;
  size?: keyof typeof HEIGHT;
  full?: boolean;
  style?: CSSProperties;
}

/** Pill button. Visual heights below 44px get a 44px touch area via `.hit`. */
export function Btn({ variant = 'outline', size = 'md', full, style, className, children, type = 'button', ...rest }: BtnProps) {
  const v = VARIANTS[variant];
  return (
    <button
      type={type}
      className={`press hit ${className ?? ''}`}
      style={{
        height: HEIGHT[size], padding: '0 16px', borderRadius: R.pill, border: `1px solid ${v.bd}`, background: v.bg, color: v.fg,
        font: font(size === 'lg' ? 700 : 600, SIZE[size]), width: full ? '100%' : undefined, flex: full ? undefined : 'none',
        whiteSpace: 'nowrap', transition: 'background .2s, color .2s, border-color .2s', ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Chip({ label, selected, onClick, dark, height = 36 }: { label: string; selected: boolean; onClick: () => void; dark?: boolean; height?: number }) {
  const s = dark
    ? selected ? { bg: C.white, fg: C.ink, bd: C.white } : { bg: 'transparent', fg: C.white, bd: C.darkChip }
    : selected ? { bg: C.ink, fg: C.white, bd: C.ink } : { bg: C.white, fg: C.ink, bd: C.border };
  return (
    <button type="button" aria-pressed={selected} onClick={onClick} className="press hit"
      style={{ flex: 'none', height, padding: '0 14px', borderRadius: R.pill, border: `1px solid ${s.bd}`, background: s.bg, color: s.fg, font: font(600, 12), transition: 'all .2s', whiteSpace: 'nowrap' }}>
      {label}
    </button>
  );
}

export function ChipRow({ children, padding = '12px 20px 0', wrap }: { children: ReactNode; padding?: string; wrap?: boolean }) {
  return <div className="noscroll" style={{ display: 'flex', gap: 6, overflowX: wrap ? undefined : 'auto', flexWrap: wrap ? 'wrap' : undefined, padding }}>{children}</div>;
}

export function Segmented<T extends string>({ options, value, onChange, label }: { options: readonly T[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div role="group" aria-label={label} style={{ display: 'grid', gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))`, gap: 4, background: C.subtle, borderRadius: R.pill, padding: 4 }}>
      {options.map((o) => (
        <button key={o} type="button" aria-pressed={value === o} onClick={() => onChange(o)} className="hit"
          style={{ height: 40, borderRadius: R.pill, border: 0, background: value === o ? C.ink : 'transparent', color: value === o ? C.white : C.ink, font: font(600, 12), transition: 'all .2s', padding: '0 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {o}
        </button>
      ))}
    </div>
  );
}

export function Eyebrow({ children, color = C.ink, spacing = 0.24, style }: { children: ReactNode; color?: string; spacing?: number; style?: CSSProperties }) {
  return <span style={{ font: font(600, 10), letterSpacing: `${spacing}em`, color, textTransform: 'uppercase', ...style }}>{children}</span>;
}

export function Mono({ children, color = C.grey, size = 11, weight = 500, style }: { children: ReactNode; color?: string; size?: number; weight?: number; style?: CSSProperties }) {
  return <span style={{ font: font(weight, size, 1.3, 'mono'), color, ...style }}>{children}</span>;
}

export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <section style={{ margin: '14px 12px 0', background: C.white, border: `1px solid ${C.subtle}`, borderRadius: R.card, padding: 18, ...style }}>{children}</section>;
}

export function DarkCard({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <section style={{ margin: '14px 12px 0', background: C.ink, color: C.white, borderRadius: R.card, padding: 18, display: 'flex', flexDirection: 'column', gap: 12, ...style }}>{children}</section>;
}

export function CardTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
      <h2 style={{ margin: 0, font: font(700, 22), letterSpacing: '-.03em' }}>{children}</h2>
      {right}
    </div>
  );
}

/** Two-letter initials for avatars ("Sofia Reyes" → "SR"). */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export function Avatar({ person, size = 36, ring }: { person: Ambassador; size?: number; ring?: string }) {
  // An uploaded photo replaces the initials; if it fails to load, the initials come back.
  const [failed, setFailed] = useState<string | null>(null);
  if (person.photoUrl && failed !== person.photoUrl) {
    return (
      <img src={person.photoUrl} alt="" aria-hidden="true" width={size} height={size} loading="lazy" decoding="async" onError={() => setFailed(person.photoUrl!)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: ring ? `2px solid ${ring}` : `1px solid ${C.border}`, flex: 'none', background: person.avatar.bg }} />
    );
  }
  return (
    <span aria-hidden="true"
      style={{ width: size, height: size, borderRadius: '50%', background: person.avatar.bg, color: person.avatar.fg, border: ring ? `2px solid ${ring}` : `1px solid ${C.border}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', font: font(700, Math.max(10, Math.round(size * 0.34)), 1), flex: 'none' }}>
      {initials(person.name)}
    </span>
  );
}

export function Tag({ label, bg, fg, radius = R.chip, style }: { label: string; bg: string; fg: string; radius?: number; style?: CSSProperties }) {
  return <span style={{ font: font(700, 9), letterSpacing: '.14em', background: bg, color: fg, padding: '5px 8px', borderRadius: radius, whiteSpace: 'nowrap', ...style }}>{label}</span>;
}

export function StageTag({ stage, style }: { stage: Stage; style?: CSSProperties }) {
  const [l, bg, fg] = STAGE_CHIP[stage];
  return <Tag label={l} bg={bg} fg={fg} style={style} />;
}

export function ProgressBar({ ratio, dark, height = 8, label }: { ratio: number; dark?: boolean; height?: number; label: string }) {
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)}
      style={{ height, borderRadius: height, background: dark ? C.dark3 : C.subtle, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${ratio * 100}%`, background: C.mark, borderRadius: height, transition: 'width .5s ease' }} />
    </div>
  );
}

export function Toggle({ on, onToggle, label, sub, disabled }: { on: boolean; onToggle: () => void; label: string; sub?: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-disabled={disabled || undefined} onClick={onToggle}
      style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 0', border: 0, background: 'transparent', textAlign: 'left', minHeight: 52 }}>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ font: font(600, 14), color: C.ink }}>{label}</span>
        {sub && <span style={{ font: font(400, 11, 1.35), color: C.grey }}>{sub}</span>}
      </span>
      <span style={{ width: 46, height: 28, borderRadius: R.pill, background: on ? C.violet : C.border, position: 'relative', transition: 'background .2s', flex: 'none', opacity: disabled ? 0.5 : 1 }}>
        <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 22, height: 22, borderRadius: '50%', background: C.white, transition: 'left .2s' }} />
      </span>
    </button>
  );
}

/** Responsive image (WebP, srcset, lazy). Without an image, renders an ink typographic tile. */
export function Photo({ id, alt, sizes = '(max-width: 480px) 100vw, 480px', eager, pad, fallback, style }: { id?: PhotoId; alt?: string; sizes?: string; eager?: boolean; pad?: number; fallback?: string; style?: CSSProperties }) {
  if (!id) {
    return (
      <div role="img" aria-label={alt ?? fallback ?? 'No image'} style={{ width: '100%', height: '100%', background: C.ink, display: 'flex', alignItems: 'flex-end', padding: 14, ...style }}>
        <span style={{ font: font(800, 24, 1), color: C.rim, letterSpacing: '-.03em' }}>{fallback}</span>
      </div>
    );
  }
  const art = PHOTOS[id].kind === 'artwork';
  return (
    <img src={photoUrl(id)} srcSet={photoSrcSet(id)} sizes={sizes} alt={alt ?? PHOTOS[id].alt} loading={eager ? 'eager' : 'lazy'} decoding="async"
      style={{ width: '100%', height: '100%', objectFit: art ? 'contain' : 'cover', padding: art ? pad ?? 12 : 0, display: 'block', ...style }} />
  );
}

export function FieldLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return <label htmlFor={htmlFor} style={{ font: font(600, 10), letterSpacing: '.24em', textTransform: 'uppercase' }}>{children}</label>;
}

export function FieldError({ children }: { children: ReactNode }) {
  return <span role="alert" style={{ font: font(500, 12, 1.4), color: C.red }}>{children}</span>;
}

export const inputStyle: CSSProperties = { height: 50, border: `1px solid ${C.border}`, borderRadius: R.input, padding: '0 14px', font: font(400, 16), color: C.ink, background: C.white, width: '100%' };
export const textareaStyle: CSSProperties = { font: font(400, 16, 1.4), border: `1px solid ${C.border}`, borderRadius: R.input, padding: 12, resize: 'none', color: C.ink, width: '100%', background: C.white };

export function BackPill({ onClick, label = '‹ Back', onPhoto }: { onClick: () => void; label?: string; onPhoto?: boolean }) {
  return (
    <button type="button" onClick={onClick} className="press"
      style={{ height: 44, padding: '0 16px', borderRadius: R.pill, border: onPhoto ? 0 : `1px solid ${C.border}`, background: C.white, color: C.ink, font: font(600, 13) }}>
      {label}
    </button>
  );
}

export function EmptyState({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div style={{ margin: '16px 12px 0', background: C.white, border: `1px dashed ${C.border}`, borderRadius: R.card, padding: 24, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
      <span style={{ font: font(700, 18) }}>{title}</span>
      {body && <span style={{ font: font(400, 13, 1.45), color: C.grey }}>{body}</span>}
      {action}
    </div>
  );
}

/** Root-screen big title. */
export function ScreenTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
      <h1 style={{ margin: 0, font: font(800, 56, 0.86), letterSpacing: '-.06em' }}>{children}</h1>
      {right && <Mono size={10} style={{ paddingBottom: 6 }}>{right}</Mono>}
    </div>
  );
}

export const safeTop = (extra: number): string => `calc(env(safe-area-inset-top, 0px) + ${extra}px)`;
